import { useCallback, useEffect, useRef, useState } from 'react';
import { PdpmEstimate, postPdpmEstimate } from 'src/services/documentService/pdpmEstimate';

/**
 * BRK-1966: data layer for the PDPM CMG estimator. Fetches the estimate for a
 * patient at an anchor date and lets the caller apply what-if overrides.
 *
 * `ard` MUST be a 'YYYY-MM-DD' string, never a Date — a fresh Date object every
 * render would make the fetch effect re-run forever.
 *
 * Overrides are per-session what-ifs: they live in hook state only, are never
 * put in redux or persisted, and die on unmount. They are also dropped whenever
 * the ARD changes — a new ARD is a new auto-pull window, so carrying the old
 * window's overrides forward would silently corrupt the estimate.
 */

export const OVERRIDE_DEBOUNCE_MS = 500;

type FetchMode = 'initial' | 'recalc';

export function usePdpmEstimate(patientUuid: string | undefined, ard: string | null) {
  const [estimate, setEstimate] = useState<PdpmEstimate | null>(null);
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  // first fetch (caller shows skeleton tiles) vs an override re-POST (caller
  // dims the prior values behind a progress bar)
  const [loading, setLoading] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // monotonic request id: a response must never write state after a newer
  // request superseded it (edit #1 resolving after edit #2 would otherwise
  // leave the panel showing the estimate for a map the user has moved past)
  const requestSeq = useRef(0);
  // the live override map, read by the debounced POST at fire time so it always
  // carries every edit made during the window rather than a captured snapshot
  const overridesRef = useRef<Record<string, string>>({});
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // mirrors `estimate` for callbacks that must not re-create when it changes
  const estimateRef = useRef<PdpmEstimate | null>(null);

  const cancelPending = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  }, []);

  const fetchEstimate = useCallback(
    async (nextOverrides: Record<string, string>, mode: FetchMode) => {
      if (!patientUuid || !ard) {
        return;
      }
      const seq = ++requestSeq.current;
      if (mode === 'initial') {
        setLoading(true);
      } else {
        setRecalculating(true);
      }
      setError(null);
      try {
        const result = await postPdpmEstimate({ patientUuid, ard, overrides: nextOverrides });
        if (seq !== requestSeq.current) {
          return; // superseded
        }
        estimateRef.current = result;
        setEstimate(result);
      } catch (e: any) {
        if (seq !== requestSeq.current) {
          return;
        }
        setError(e?.message || 'Failed to calculate the PDPM estimate');
        // a failed recalc keeps the last good estimate on screen (the advisory
        // stays useful); a failed first load has nothing to keep
        if (mode === 'initial') {
          estimateRef.current = null;
          setEstimate(null);
        }
      } finally {
        if (seq === requestSeq.current) {
          setLoading(false);
          setRecalculating(false);
        }
      }
    },
    [patientUuid, ard]
  );

  // patient or ARD change: drop the what-ifs, invalidate anything in flight,
  // and re-anchor the estimate
  useEffect(() => {
    cancelPending();
    requestSeq.current += 1;
    overridesRef.current = {};
    setOverrides({});
    if (!patientUuid || !ard) {
      estimateRef.current = null;
      setEstimate(null);
      setLoading(false);
      setRecalculating(false);
      setError(null);
      return;
    }
    fetchEstimate({}, 'initial');
  }, [patientUuid, ard, fetchEstimate, cancelPending]);

  // drop a queued POST and any in-flight response when the panel goes away
  useEffect(
    () => () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      requestSeq.current += 1;
    },
    []
  );

  const applyOverrides = useCallback((next: Record<string, string>) => {
    overridesRef.current = next;
    setOverrides(next);
  }, []);

  /**
   * Typing in the drawer: the new value shows immediately and the recalculating
   * POST is debounced, so a burst of edits costs one request carrying the merged
   * map rather than one per keystroke.
   */
  const setOverride = useCallback(
    (itemId: string, value: string) => {
      applyOverrides({ ...overridesRef.current, [itemId]: value });
      cancelPending();
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        fetchEstimate(overridesRef.current, 'recalc');
      }, OVERRIDE_DEBOUNCE_MS);
    },
    [applyOverrides, cancelPending, fetchEstimate]
  );

  /** Reverting a row is a deliberate click, not typing — recalc immediately. */
  const clearOverride = useCallback(
    (itemId: string) => {
      const next = { ...overridesRef.current };
      delete next[itemId];
      applyOverrides(next);
      cancelPending();
      fetchEstimate(next, 'recalc');
    },
    [applyOverrides, cancelPending, fetchEstimate]
  );

  const resetOverrides = useCallback(() => {
    applyOverrides({});
    cancelPending();
    fetchEstimate({}, 'recalc');
  }, [applyOverrides, cancelPending, fetchEstimate]);

  /**
   * Refresh / retry. Re-runs with the current overrides; after a failed first
   * load there is no estimate to keep on screen, so it reports as `loading`
   * (skeleton) rather than `recalculating` (dimmed prior values).
   */
  const refresh = useCallback(() => {
    cancelPending();
    fetchEstimate(overridesRef.current, estimateRef.current ? 'recalc' : 'initial');
  }, [cancelPending, fetchEstimate]);

  return {
    estimate,
    overrides,
    loading,
    recalculating,
    error,
    setOverride,
    clearOverride,
    resetOverrides,
    refresh,
  };
}
